import React from 'react'
import PropTypes from 'prop-types'
import { Tag, Tooltip } from 'antd'
import {
  ATTENTION,
  KIND_LABELS,
  PRIORITY_COLORS,
  attentionLabel,
  attentionPriority,
  channelColor,
  channelLabel,
  formatAge,
  stageColor,
  stageTitle,
  statusColor,
  statusLabel,
  updatesSummary
} from './utils'

// The tags the worklist and the detail page share. They are named function components on
// purpose: an anonymous arrow inside an antd `render` trips react/display-name, and these are
// rendered from column factories.

export function StageTag({ stage, title, attention, completion }) {
  return (
    <Tag color={stageColor(stage, attention, completion)}>
      {stageTitle(stage, title)}
    </Tag>
  )
}

StageTag.propTypes = {
  stage: PropTypes.string,
  title: PropTypes.string,
  attention: PropTypes.string,
  completion: PropTypes.string
}

// Team vocabulary. A completed shipment reads "Completed · collected" in green or "Completed ·
// ended without gate-out" in grey — never the same tag for both, because only the first one means
// the cargo actually left the port.
export function StatusTag({ status, completion }) {
  return (
    <Tag color={statusColor(status, completion)}>
      {statusLabel(status, completion)}
    </Tag>
  )
}

StatusTag.propTypes = {
  status: PropTypes.string,
  completion: PropTypes.string
}

// The one red on the page, and only for `urgent`: this is the internal priority, not the customer's
// situation. The tooltip carries the words the customer is reading right now, so a team member can
// answer the phone without opening the detail page.
export function AttentionTag({ kind, priority, since }) {
  if (!kind) return null
  const copy = ATTENTION[kind] || {}
  const tag = (
    <Tag color={PRIORITY_COLORS[attentionPriority(kind, priority)]}>
      {attentionLabel(kind)} · {formatAge(since)}
    </Tag>
  )
  if (!copy.customerTitle) return tag
  return <Tooltip title={`Customer sees: "${copy.customerTitle}"`}>{tag}</Tooltip>
}

AttentionTag.propTypes = {
  kind: PropTypes.string,
  priority: PropTypes.string,
  since: PropTypes.string
}

// RoRo rides beside the kind because it explains an otherwise alarming detail page: a roll-on/
// roll-off shipment has no containers and no vessel, and that is normal.
export function KindTag({ kind, roro }) {
  return (
    <span>
      <Tag>{KIND_LABELS[kind] || kind || '—'}</Tag>
      {roro ? <Tag color="purple">RoRo</Tag> : null}
    </span>
  )
}

KindTag.propTypes = {
  kind: PropTypes.string,
  roro: PropTypes.bool
}


// Where the customer started it (spec: whatsapp-shipment-tracking Req 1.1). On the row and not
// only the detail, because "how is the web flow doing" is a worklist question — and a WhatsApp
// shipment is the one that came from a bill of lading somebody sent us.
export function ChannelTag({ channel }) {
  if (!channel) return null
  return <Tag color={channelColor(channel)}>{channelLabel(channel)}</Tag>
}

ChannelTag.propTypes = {
  channel: PropTypes.string
}

// Whether we are messaging this customer about this shipment, and when they last said not to.
// Only shown when they are OFF: "on" is the default for every shipment, and a green tag on every
// row would be noise.
export function UpdatesTag({ shipment }) {
  const summary = updatesSummary(shipment || {})
  if (summary.on) return null
  return (
    <Tooltip title="No shipment updates are being sent to this number.">
      <Tag color={summary.color}>{summary.label}</Tag>
    </Tooltip>
  )
}

UpdatesTag.propTypes = {
  shipment: PropTypes.object
}
